import { useState } from "react";

import useAxios from "@hooks/use-axios";
import { confirmAlert } from "@helpers/toast";
import { OPS_ENDPOINTS } from "@constants/endpoints";
import { brandOfPublisher, uniqueBrands } from "../misc/kyc";

// KYC reminder + force verify, shared by both publisher lists and the publisher
// page. `brand` ({ id, name }) is the brand the view is scoped to, if any;
// `onChanged(publisher)` runs after a force verify so the caller can refresh.
const usePublisherKyc = ({ brand, onChanged } = {}) => {
    // Separate instances so sending a reminder doesn't spin the Force verify button.
    const { axiosInstance: remindAxios, loading: sending } = useAxios({ base: "ops" });
    const { axiosInstance: verifyAxios, loading: verifying } = useAxios({ base: "ops" });
    const [reminder, setReminder] = useState(null);

    // Every brand the reminder could go out under — the row's own, the view's, and
    // any the caller knows about (the publisher page passes the brands from invoices).
    const reminderBrandsFor = (publisher, extraBrands = []) => uniqueBrands([brandOfPublisher(publisher, brand), ...extraBrands]);

    // A single publisher, or every incomplete one of the brand at once.
    const openReminder = (publisher, extraBrands) => {
        const brands = reminderBrandsFor(publisher, extraBrands);
        setReminder({ publishers: [publisher], brands, brand: brands[0] });
    };
    const openBulkReminder = (publishers) => setReminder({ bulk: true, publishers, brands: [brand], brand });
    const selectReminderBrand = (brandId) => setReminder((prev) => ({ ...prev, brand: prev.brands.find((option) => option.id === brandId) }));
    const closeReminder = () => setReminder(null);

    const sendReminder = async () => {
        // The KYC endpoint names the holder explicitly; list rows only carry `_id`.
        const [target] = reminder.publishers;
        const body = reminder.bulk ? undefined : { holder_id: target._id };
        try {
            await remindAxios.post(OPS_ENDPOINTS.BRAND_KYC_REMINDER(reminder.brand.id), body);
            closeReminder();
        } catch {
            // useAxios already surfaced the error — keep the preview open to retry.
        }
    };

    const forceVerify = async (publisher) => {
        const { isConfirmed } = await confirmAlert({
            title            : "Force verify KYC?",
            text             : `${publisher.holder_name || "This publisher"} will be marked verified and their payouts released. Only do this after checking the documents yourself.`,
            icon             : "warning",
            confirmButtonText: "Force verify"
        });
        if (!isConfirmed) return;
        try {
            await verifyAxios.patch(OPS_ENDPOINTS.PUBLISHER_KYC_VERIFY(publisher._id));
            await onChanged?.({ ...publisher, kyc_status: "verified" });
        } catch {
            // useAxios already surfaced the error.
        }
    };

    return {
        reminder,
        openReminder,
        openBulkReminder,
        selectReminderBrand,
        closeReminder,
        sendReminder,
        sending,
        forceVerify,
        verifying,
        // Reminders go out under a brand's name — without one the action can't be offered.
        canSendFor: (publisher, extraBrands) => reminderBrandsFor(publisher, extraBrands).length > 0
    };
};

export default usePublisherKyc;
